import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/builder/templates/rating-drop-notify/generator.tsx
// Watches the rolling average of the last N reviews. Fires a webhook when the
// average dips below the threshold.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { useReviews } from '../../../sdk';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return _jsx(RatingDropUi, { name: ctx.instanceName, cfg: cfg });
};
function RatingDropUi({ name, cfg }) {
    const { reviews } = useReviews({ limit: cfg.windowSize });
    const rows = reviews ?? [];
    const avg = rows.length ? (rows.reduce((s, r) => s + (r.rating ?? 0), 0) / rows.length) : null;
    const below = avg !== null && avg < cfg.threshold;
    return (_jsxs("main", { id: "main", className: "mx-auto max-w-3xl px-6 py-10", children: [_jsx("h1", { className: "text-2xl font-semibold", children: name }), _jsxs("p", { className: "mt-1 text-sm text-slate-500", children: ["Alert below ", cfg.threshold.toFixed(1), "\u2605 (window: last ", cfg.windowSize, " reviews)."] }), _jsxs("div", { className: `mt-6 rounded-2xl p-5 ring-1 ${below ? 'bg-red-50 ring-red-200' : 'bg-emerald-50 ring-emerald-200'}`, children: [_jsxs("p", { className: "text-sm font-medium text-slate-900", children: ["Current average: ", _jsx("strong", { children: avg === null ? '—' : avg.toFixed(2) + '★' }), " over ", rows.length, " reviews"] }), _jsx("p", { className: "mt-1 text-sm text-slate-600", children: below ? 'Below your threshold. An alert will fire on the next new review.' : "You're above your threshold. Nice." })] })] }));
}
const runtimeHooks = {
    async onReviewCreated(ctx) {
        const cfg = ctx.config;
        if (!cfg.webhookUrl)
            return;
        if (!ctx.permissions.includes('external:fetch'))
            return;
        // The host is expected to batch-call hooks with the fresh review;
        // we pull the recent average ourselves via Supabase.
        if (!ctx.orgId)
            return;
        const { supabase } = await import('../../../../supabase');
        const { data } = await supabase.from('reviews').select('rating').eq('org_id', ctx.orgId)
            .order('posted_at', { ascending: false }).limit(cfg.windowSize);
        const rows = (data ?? []);
        if (rows.length === 0)
            return;
        const avg = rows.reduce((s, r) => s + (r.rating ?? 0), 0) / rows.length;
        if (avg >= cfg.threshold)
            return;
        await fetch(cfg.webhookUrl, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                alert: 'rating-drop', average: avg, threshold: cfg.threshold, window: cfg.windowSize,
                source: 'plugin:builder:rating-drop-notify',
            }),
        }).catch(() => { });
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Alerts when the last ${cfg.windowSize} reviews average below ${cfg.threshold}★.`,
        category: 'automation', icon: 'AlertTriangle',
        permissions: ['read:reviews', 'external:fetch', 'ui:toast'],
        entry: 'both', hooks: { onReviewCreated: true },
        config: meta.configSchema,
        defaultConfig: { threshold: cfg.threshold, windowSize: cfg.windowSize, webhookUrl: cfg.webhookUrl },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useReviews } from '../sdk';
export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: ${Number(cfg.windowSize)} });
  const rows = reviews ?? [];
  const avg = rows.length ? (rows.reduce((s, r) => s + (r.rating ?? 0), 0) / rows.length) : null;
  const below = avg !== null && avg < ${Number(cfg.threshold)};
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <p className="mt-1 text-sm text-slate-500">Alert below ${Number(cfg.threshold).toFixed(1)}★.</p>
      <div className={'mt-6 rounded-2xl p-5 ring-1 ' + (below ? 'bg-red-50 ring-red-200' : 'bg-emerald-50 ring-emerald-200')}>
        <p className="text-sm font-medium">Average: {avg === null ? '—' : avg.toFixed(2) + '★'} ({rows.length} reviews)</p>
      </div>
    </main>
  );
}
`;
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';
import { supabase } from '../../supabase';
export const handlers: PluginHandlers = {
  async onReviewCreated(ctx: HandlerContext) {
    if (!ctx.permissions.includes('external:fetch')) return;
    if (!ctx.orgId) return;
    const { data } = await supabase.from('reviews').select('rating').eq('org_id', ctx.orgId).order('posted_at', { ascending: false }).limit(${Number(cfg.windowSize)});
    const rows = (data ?? []) as { rating: number | null }[];
    if (rows.length === 0) return;
    const avg = rows.reduce((s, r) => s + (r.rating ?? 0), 0) / rows.length;
    if (avg >= ${Number(cfg.threshold)}) return;
    await fetch('${esc(cfg.webhookUrl)}', { method: 'POST', headers: {'content-type':'application/json'}, body: JSON.stringify({ alert: 'rating-drop', average: avg, threshold: ${Number(cfg.threshold)}, window: ${Number(cfg.windowSize)} }) }).catch(() => {});
  },
};
export default handlers;
`;
    return assemble({ manifest, pluginTsx, hooksTs,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Posts an alert to your webhook when the rolling average drops below ${cfg.threshold}★.`,
            configSummary: [['threshold', String(cfg.threshold)], ['windowSize', String(cfg.windowSize)], ['webhookUrl', cfg.webhookUrl]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => `Rating alarm under ${cfg.threshold}★`,
    describe: (cfg) => `Send an alert to your webhook whenever the last ${cfg.windowSize} reviews average below ${cfg.threshold}★.`,
    generate, runtimeUi: RuntimeUi, runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
